/**
 * Storage path utilities for team-aware file organization
 * 
 * Path Structure:
 * - Personal: personal/{user_id}/{appraisal_id}/{subfolder}/
 * - Team: teams/{team_id}/{appraisal_id}/{subfolder}/
 * - Assets: {category}/{user_id}/{filename}
 */

/**
 * USPAP-aligned workfile subfolders
 */
export type StorageSubfolder = 
  | 'engagement'      // Signed ELs, fee quotes, contracts
  | 'inspection'      // Subject photos, field notes, sketches
  | 'research'        // MLS sheets, deeds, zoning, tax records
  | 'correspondence'  // Client emails, engagement logs
  | 'finance'         // Invoices, payment receipts
  | 'versions';       // Transmitted PDFs (v1.pdf, v2_revised.pdf)

/**
 * Asset categories for the assets bucket
 */
export type AssetCategory = 'signatures' | 'avatars' | 'templates';

/**
 * Generates the storage path for an appraisal's files.
 * Uses team path for team appraisals, personal path for individual appraisals.
 * 
 * @param userId - The user's ID
 * @param appraisalId - The appraisal's ID
 * @param teamId - Optional team ID (null for personal appraisals)
 * @param subfolder - The type of files being stored
 * @returns The storage path without leading slash
 */
export function getAppraisalStoragePath(
  userId: string,
  appraisalId: string,
  teamId: string | null,
  subfolder: StorageSubfolder = 'inspection'
): string {
  if (teamId) {
    return `teams/${teamId}/${appraisalId}/${subfolder}`;
  }
  return `personal/${userId}/${appraisalId}/${subfolder}`;
}

/**
 * Extracts the base path (without subfolder) for an appraisal
 */
export function getAppraisalBasePath(
  userId: string,
  appraisalId: string,
  teamId: string | null
): string {
  if (teamId) {
    return `teams/${teamId}/${appraisalId}`;
  }
  return `personal/${userId}/${appraisalId}`;
}

/**
 * Generates the full file path for a specific file
 */
export function getAppraisalFilePath(
  userId: string,
  appraisalId: string,
  teamId: string | null,
  subfolder: StorageSubfolder,
  fileName: string
): string {
  return `${getAppraisalStoragePath(userId, appraisalId, teamId, subfolder)}/${fileName}`;
}

/**
 * Generates the path for a user's asset file (signature, avatar, template)
 * Path format: {category}/{user_id}/{filename}
 * 
 * @param userId - The user's ID
 * @param category - The type of asset
 * @param fileName - The file name
 * @returns The asset path for the assets bucket
 */
export function getAssetPath(
  userId: string,
  category: AssetCategory,
  fileName: string
): string {
  return `${category}/${userId}/${fileName}`;
}

/**
 * Maps legacy subfolder names to new USPAP-aligned subfolders
 * Used for backward compatibility during migration
 */
export function mapLegacySubfolder(legacyName: string): StorageSubfolder {
  const mapping: Record<string, StorageSubfolder> = {
    'photos': 'inspection',
    'sketch': 'inspection',
    'documents': 'research',
    'supporting': 'research',
    'comparables': 'research',
    'other': 'research',
  };
  return mapping[legacyName] || 'research';
}

/**
 * Checks if a path uses the legacy format (no personal/ prefix)
 */
export function isLegacyPath(path: string): boolean {
  const firstSegment = path.split('/')[0];
  return firstSegment !== 'personal' && firstSegment !== 'teams';
}
